import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { EIGENVALUE_CALCULATOR_STRINGS } from "@/components/tools/eigenvalue-calculator";
import { MatrixView } from "@/components/tools/linear-algebra/matrix-view";
import { t } from "@/lib/i18n/interpolate";
import type {
  ExactEigen,
  ExactEigenvalue,
  NumericEigen,
  NumericEigenvalue,
  Poly,
} from "@/lib/tool-kit/linear-algebra/eigen";
import type { Complex } from "@/lib/tool-kit/linear-algebra/numeric-eigen";
import {
  formatQuad,
  type QuadNumber,
  quadToComplex,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  formatPolynomial,
  formatStyled,
  intR,
  mulR,
  type NumberStyle,
  negR,
  type Rational,
  signR,
  subR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof EIGENVALUE_CALCULATOR_STRINGS;

export interface EigenCtx {
  strings: Strings;
  style: NumberStyle;
  mark: string;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}

/** `REAL_TOLERANCE` (1e−9) as it reads in the note. */
const TOLERANCE_TEXT = "10⁻⁹";

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

function superscript(n: number): string {
  return String(n)
    .split("")
    .map((digit) => SUPERSCRIPTS[Number(digit)])
    .join("");
}

function lambdaPower(k: number): string {
  if (k === 0) {
    return "";
  }
  return k === 1 ? "λ" : `λ${superscript(k)}`;
}

/** p(λ) as text: "λ² − 7λ + 10". */
export function polynomialText(poly: Poly, ctx: EigenCtx): string {
  const terms = poly
    .map((coefficient, k) => ({ coefficient, variable: lambdaPower(k) }))
    .reverse();
  return formatPolynomial(terms, ctx.style, ctx.mark);
}

function rootFactor(
  root: Rational,
  multiplicity: number,
  ctx: EigenCtx
): string {
  let base = "λ";
  if (signR(root) !== 0) {
    const magnitude = formatStyled(
      signR(root) > 0 ? root : negR(root),
      ctx.style,
      ctx.mark
    );
    base = `(λ ${signR(root) > 0 ? "−" : "+"} ${magnitude})`;
  }
  return multiplicity === 1 ? base : `${base}${superscript(multiplicity)}`;
}

function factoredText(
  solution: ExactEigen | NumericEigen,
  ctx: EigenCtx
): string | null {
  const { roots, remainder } = solution.factorization;
  if (roots.length === 0) {
    return null;
  }
  const parts = roots.map((r) => rootFactor(r.root, r.multiplicity, ctx));
  if (remainder.length > 1) {
    parts.push(`(${polynomialText(remainder, ctx)})`);
  }
  return parts.join("");
}

/* -------------------------------------------------------------- formatting */

function cleaned(value: number): number {
  return Math.abs(value) < 1e-10 ? 0 : value;
}

export function formatComplex(c: Complex, ctx: EigenCtx): string {
  const re = cleaned(c.re);
  const im = cleaned(c.im);
  const digits = { maximumFractionDigits: 6 };
  if (im === 0) {
    return ctx.fmt(re, digits);
  }
  const unit = Math.abs(Math.abs(im) - 1) < 1e-9;
  const imagine = unit ? "i" : `${ctx.fmt(Math.abs(im), digits)}i`;
  if (re === 0) {
    return `${im < 0 ? "−" : ""}${imagine}`;
  }
  return `${ctx.fmt(re, digits)} ${im < 0 ? "−" : "+"} ${imagine}`;
}

function Column({ entries, label }: { entries: string[]; label: string }) {
  return (
    <table
      aria-label={label}
      className="inline-block border-collapse border-foreground/70 border-x-2 align-middle font-mono text-foreground text-sm"
    >
      <tbody>
        {entries.map((entry, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: vector entries are positional
          <tr key={i}>
            <td className="px-2.5 py-0.5 text-right">{entry}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Check({ children }: { children: string }) {
  return <p className="text-muted-foreground text-sm">{children}</p>;
}

/* ------------------------------------------------------------- polynomial */

function polynomialWorking(solution: ExactEigen | NumericEigen, ctx: EigenCtx) {
  const { strings } = ctx;
  const v = (x: Rational) => formatStyled(x, ctx.style, ctx.mark);
  const n = solution.size;
  const poly = solution.charPoly;
  const text = polynomialText(poly, ctx);
  if (n === 2) {
    return t(strings.polyWorking2, {
      trace: v(solution.trace),
      det: v(solution.determinant),
      poly: text,
    });
  }
  if (n === 3) {
    return t(strings.polyWorking3, {
      trace: v(solution.trace),
      c2: v(poly[1]),
      det: v(solution.determinant),
      poly: text,
    });
  }
  return t(strings.polyWorkingN, { poly: text });
}

function discriminantLine(remainder: Poly, ctx: EigenCtx): string {
  const [c, b, a] = remainder;
  const disc = subR(mulR(b, b), mulR(intR(4), mulR(a, c)));
  return t(ctx.strings.quadraticStep, {
    quadratic: polynomialText(remainder, ctx),
    disc: formatStyled(disc, ctx.style, ctx.mark),
  });
}

function RootSearch({
  solution,
  ctx,
}: {
  solution: ExactEigen | NumericEigen;
  ctx: EigenCtx;
}) {
  const { strings } = ctx;
  const { roots, remainder, searched } = solution.factorization;
  const lines: string[] = [];
  if (roots.length > 0) {
    lines.push(
      t(strings.rationalRoots, {
        roots: roots
          .map((r) =>
            r.multiplicity === 1
              ? formatStyled(r.root, ctx.style, ctx.mark)
              : t(strings.rootTimes, {
                  root: formatStyled(r.root, ctx.style, ctx.mark),
                  n: ctx.fmt(r.multiplicity),
                })
          )
          .join(", "),
      })
    );
  } else if (searched) {
    lines.push(strings.noRationalRoots);
  }
  if (solution.mode === "exact" && remainder.length === 3) {
    lines.push(discriminantLine(remainder, ctx));
  }
  if (solution.mode === "numeric") {
    lines.push(strings.numericReason);
  }
  return (
    <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------- eigenvalues */

function ExactCard({ e, ctx }: { e: ExactEigenvalue; ctx: EigenCtx }) {
  const { strings } = ctx;
  const name = formatQuad(e.value, ctx.style, ctx.mark);
  const approx = quadToComplex(e.value);
  const exactInDecimals = e.value.b.num === BigInt(0);
  return (
    <div className={`${CARD} space-y-3`}>
      <p className="font-semibold text-foreground">
        {t(strings.lambdaIs, { value: name })}
      </p>
      <p className="text-muted-foreground text-sm">
        {exactInDecimals
          ? ""
          : `${t(strings.decimalValue, { value: formatComplex(approx, ctx) })} · `}
        {t(strings.multiplicities, {
          algebraic: ctx.fmt(e.algebraic),
          geometric: ctx.fmt(e.geometric),
        })}
      </p>
      {e.shifted ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">{strings.shiftedMatrix}</span>
          <MatrixView
            label={strings.shiftedMatrix}
            mark={ctx.mark}
            matrix={e.shifted}
            style={ctx.style}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">
          {e.vectors.length > 1
            ? strings.eigenvectorsPlural
            : strings.eigenvector}
        </span>
        {e.vectors.map((vector) => (
          <Column
            entries={vector.map((x: QuadNumber) =>
              formatQuad(x, ctx.style, ctx.mark)
            )}
            key={vector.map((x) => formatQuad(x)).join("|")}
            label={strings.eigenvector}
          />
        ))}
      </div>
    </div>
  );
}

function NumericCard({ e, ctx }: { e: NumericEigenvalue; ctx: EigenCtx }) {
  const { strings } = ctx;
  return (
    <div className={`${CARD} space-y-3`}>
      <p className="font-semibold text-foreground">
        {t(strings.lambdaIs, { value: formatComplex(e.value, ctx) })}
      </p>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">{strings.eigenvector}</span>
        <Column
          entries={e.vector.map((x) => formatComplex(x, ctx))}
          label={strings.eigenvector}
        />
      </div>
    </div>
  );
}

function Diagonalization({
  solution,
  ctx,
}: {
  solution: ExactEigen;
  ctx: EigenCtx;
}) {
  const { strings } = ctx;
  if (!solution.diagonalization) {
    return null;
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.diagonalizationHeading}</h3>
      <p className="text-muted-foreground text-sm">
        {strings.diagonalizationNote}
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <span className="font-mono text-foreground text-sm">P =</span>
        <MatrixView
          label="P"
          mark={ctx.mark}
          matrix={solution.diagonalization.p}
          style={ctx.style}
        />
        <span className="font-mono text-foreground text-sm">D =</span>
        <MatrixView
          label="D"
          mark={ctx.mark}
          matrix={solution.diagonalization.d}
          style={ctx.style}
        />
      </div>
    </>
  );
}

export function EigenResults({
  solution,
  ctx,
}: {
  solution: ExactEigen | NumericEigen;
  ctx: EigenCtx;
}) {
  const { strings } = ctx;
  const v = (x: Rational) => formatStyled(x, ctx.style, ctx.mark);
  const names =
    solution.mode === "exact"
      ? solution.eigenvalues.map((e) =>
          formatQuad(e.value, ctx.style, ctx.mark)
        )
      : solution.eigenvalues.map((e) => formatComplex(e.value, ctx));
  const factored = factoredText(solution, ctx);
  const expanded =
    solution.mode === "exact"
      ? solution.eigenvalues.flatMap((e) =>
          Array.from({ length: e.algebraic }, () =>
            formatQuad(e.value, ctx.style, ctx.mark)
          )
        )
      : names;
  return (
    <>
      <div className={`${RESULT_CARD} space-y-2`}>
        <p className="text-muted-foreground text-sm">
          {strings.eigenvaluesLabel}
        </p>
        <p className="break-words font-semibold text-foreground text-xl">
          {expanded
            .map((name, i) => `λ${ctx.fmt(i + 1)} = ${name}`)
            .join(",  ")}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyEigenvalues}
          small
          text={expanded.join(", ")}
        />
      </div>
      <StatGrid>
        <StatCard label={strings.traceLabel} value={v(solution.trace)} />
        <StatCard
          label={strings.determinantLabel}
          value={v(solution.determinant)}
        />
        <StatCard
          label={strings.methodLabel}
          value={
            solution.mode === "exact"
              ? strings.methodExact
              : strings.methodNumeric
          }
        />
        <StatCard
          label={strings.symmetricLabel}
          value={solution.symmetric ? strings.yes : strings.no}
        />
        {solution.mode === "exact" ? (
          <StatCard
            label={strings.diagonalizableLabel}
            value={solution.diagonalizable ? strings.yes : strings.no}
          />
        ) : null}
      </StatGrid>
      <Check>
        {t(strings.traceCheck, {
          trace: v(solution.trace),
          det: v(solution.determinant),
        })}
      </Check>
      <h3 className={WIDGET_H3}>{strings.polynomialHeading}</h3>
      <div className="space-y-2 font-mono text-foreground text-sm">
        <p>{polynomialWorking(solution, ctx)}</p>
        {factored ? <p>{t(strings.factoredLine, { factored })}</p> : null}
      </div>
      <RootSearch ctx={ctx} solution={solution} />
      <h3 className={WIDGET_H3}>{strings.eigenvectorsHeading}</h3>
      {solution.mode === "exact" ? (
        <>
          <Check>{strings.exactVectorNote}</Check>
          {solution.eigenvalues.map((e) => (
            <ExactCard ctx={ctx} e={e} key={formatQuad(e.value)} />
          ))}
          <Diagonalization ctx={ctx} solution={solution} />
          {solution.diagonalizable ? null : (
            <Notice>{strings.defective}</Notice>
          )}
        </>
      ) : (
        <>
          <Check>
            {t(strings.numericNote, {
              tolerance: TOLERANCE_TEXT,
            })}
          </Check>
          {solution.eigenvalues.map((e, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: eigenvalues repeat, so only position identifies them
            <NumericCard ctx={ctx} e={e} key={i} />
          ))}
        </>
      )}
    </>
  );
}
